import { CatalogPartView } from "src/api/generated/erp/db/types/tables/catalogPartView.ts";
import { FormCommonProps } from "src/components/common/forms/types.ts";
import {
	CopyCatalogPartToSitesFormApi,
	CopyCatalogPartToSitesFormApi_FormInitData,
} from "src/api/generated/erp/parts/catalogPart/api/copyCatalogPartToSitesFormApi.ts";
import i18n from "i18next";
import { CatalogPartToSiteParams } from "src/api/generated/erp/parts/catalogPart/service/catalogPartToSiteParams.ts";
import { Control, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { useAavoToast } from "src/components/common/toast/AavoToastContext";
import { getPartTypeLabels } from "src/api/generated/erp/db/types/enums/partType.ts";
import { FormEnumSelectField } from "src/components/common/forms/fields/FormEnumSelectField.tsx";
import { Divider, Typography } from "@mui/material";
import { requireRule } from "src/components/common/forms/validation.ts";
import { getAcquisitionMethodLabels } from "src/api/generated/erp/db/types/enums/acquisitionMethod.ts";
import { FormAsyncUserSelectField } from "src/components/views/users/FormAsyncUserSelectField.tsx";
import { FormNumberField } from "src/components/common/forms/fields/FormNumberField.tsx";
import { AsyncForm } from "src/components/common/forms/AsyncForm.tsx";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { Fragment } from "react";
import { getPartValuationMethodLabels } from "src/api/generated/erp/db/types/enums/partValuationMethod.ts";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField";
import { SiteView } from "src/api/generated/erp/db/types/tables/siteView.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface CopyCatalogPartToSitesFormProps extends FormCommonProps<number[]> {
	catalogParts: CatalogPartView[];
	sites: SiteView[];
}

export const CopyCatalogPartToSitesForm = ({
	catalogParts,
	sites,
	onFormEdited,
	onCompleted,
}: CopyCatalogPartToSitesFormProps) => {
	const { showToast } = useAavoToast();

	return (
		<AsyncForm<CopyCatalogPartToSitesFormApi_FormInitData, FormValues, number[]>
			fetch={() => CopyCatalogPartToSitesFormApi.getFormInitData({ siteIds: sites.map((s) => s.siteId) })}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			agentContext={{
				key: defineAgentUiContextKey("erp.parts.copyCatalogPartsToSites.form"),
				data: {
					catalogPartIds: catalogParts.map((catalogPart) => catalogPart.catalogPartId),
					siteIds: sites.map((site) => site.siteId),
				},
			}}
			getDefaultValues={() => {
				return {
					catalogParts: catalogParts.map((catalogPart) => ({
						catalogPart: catalogPart,
						sites: sites.map((site) => ({
							catalogPartId: catalogPart.catalogPartId,
							siteId: site.siteId,
							partType: "PURCHASE",
							acquisitionMethod: "WAREHOUSE",
							responsiblePersonId: catalogPart.responsiblePersonId,
							warehouseTransferUnitId: catalogPart.partUnitId,
							warehouseTransferFactor: 1,
							valuationMethod: "STANDARD_COST",
							cost: 0,
							salesRemark: "",
							acquisitionRemark: "",
						})),
					})),
				};
			}}
			render={({ data, control, watch, setValue }) => getCatalogParts(control, watch, setValue, data)}
			submit={async ({ catalogParts }) => {
				const result = await CopyCatalogPartToSitesFormApi.savePartToSites({
					parts: catalogParts.flatMap((part) => part.sites),
				});
				showToast({ title: i18n.t("parts_copied_to_sites") });
				return result;
			}}
		/>
	);

	function getCatalogParts(
		control: Control<FormValues>,
		watch: UseFormWatch<FormValues>,
		setValue: UseFormSetValue<FormValues>,
		data: CopyCatalogPartToSitesFormApi_FormInitData,
	) {
		return catalogParts.map((catalogPart, catalogPartIndex) => (
			<Fragment key={catalogPart.catalogPartId}>
				<Divider
					sx={{
						gridColumn: "1 / -1",
						color: "primary.main",
					}}
					textAlign={"left"}
				>
					{`${catalogPart.partNo} ${catalogPart.description_1} ${catalogPart.description_2}`}
				</Divider>
				{getPartSites(catalogPartIndex, control, watch, setValue, data)}
			</Fragment>
		));
	}

	function getPartSites(
		catalogPartIndex: number,
		control: Control<FormValues>,
		watch: UseFormWatch<FormValues>,
		setValue: UseFormSetValue<FormValues>,
		data: CopyCatalogPartToSitesFormApi_FormInitData,
	) {
		return sites.map((site, siteIndex) => {
			return site == null ?
					<></>
				:	<SingleSitePartContent
						key={site.siteId}
						siteIndex={siteIndex}
						catalogPartIndex={catalogPartIndex}
						control={control}
						site={site}
						watch={watch}
						setValue={setValue}
						data={data}
					/>;
		});
	}
};

interface SingleUnitContentProps {
	siteIndex: number;
	catalogPartIndex: number;
	control: Control<FormValues>;
	watch: UseFormWatch<FormValues>;
	setValue: UseFormSetValue<FormValues>;
	site: SiteView;
	data: CopyCatalogPartToSitesFormApi_FormInitData;
}

const SingleSitePartContent = ({
	control,
	catalogPartIndex,
	siteIndex,
	watch,
	setValue,
	site,
	data,
}: SingleUnitContentProps) => {
	const unitOptions = data.erpUnits;
	const productionLinesBySiteId = data.productionLineOptionsBySiteId;
	const capacityQuantityField = `catalogParts.${catalogPartIndex}.sites.${siteIndex}.capacityQuantity` as const;
	return (
		<VerticalBox sx={{ gap: 1 }}>
			<Typography
				sx={{
					color: "secondary.main",
				}}
			>{`${i18n.t("site")}: ${site.siteName}`}</Typography>
			<HorizontalBox
				sx={{
					gridColumnGap: "0.25rem",
					"& > *": { flex: 1 },
				}}
			>
				<>
					<FormEnumSelectField
						control={control}
						name={`catalogParts.${catalogPartIndex}.sites.${siteIndex}.partType`}
						label={i18n.t("part_type")}
						options={getPartTypeLabels()}
						rules={requireRule()}
						onChange={(newPartType) => {
							if (newPartType !== "MANUFACTURE") {
								setValue(capacityQuantityField, null);
							} else if (watch(capacityQuantityField) == null) {
								setValue(capacityQuantityField, 1);
							}
						}}
					/>
					<FormEnumSelectField
						options={getAcquisitionMethodLabels()}
						control={control}
						name={`catalogParts.${catalogPartIndex}.sites.${siteIndex}.acquisitionMethod`}
						label={i18n.t("acquisition_method")}
						rules={requireRule()}
					/>
					<FormAsyncUserSelectField
						label={i18n.t("responsible_person")}
						control={control}
						name={`catalogParts.${catalogPartIndex}.sites.${siteIndex}.responsiblePersonId`}
						rules={requireRule()}
					/>
					<FormSelectField
						control={control}
						name={`catalogParts.${catalogPartIndex}.sites.${siteIndex}.warehouseTransferUnitId`}
						label={i18n.t("warehouse_transfer_unit")}
						options={unitOptions}
						getOptionKey={(option) => option.unitId}
						getOptionLabel={(option) => option.unitName}
						rules={requireRule()}
					/>
					<FormNumberField
						control={control}
						name={`catalogParts.${catalogPartIndex}.sites.${siteIndex}.warehouseTransferFactor`}
						label={i18n.t("warehouse_transfer_factor")}
						rules={requireRule()}
					/>
					<FormNumberField
						control={control}
						name={`catalogParts.${catalogPartIndex}.sites.${siteIndex}.cost`}
						label={i18n.t("cost")}
						rules={requireRule()}
					/>
					<FormEnumSelectField
						control={control}
						name={`catalogParts.${catalogPartIndex}.sites.${siteIndex}.valuationMethod`}
						label={i18n.t("valuation_method")}
						options={getPartValuationMethodLabels()}
						rules={requireRule()}
					/>
					{watch(`catalogParts.${catalogPartIndex}.sites.${siteIndex}.partType`) == "MANUFACTURE" && (
						<>
							<FormSelectField
								control={control}
								name={`catalogParts.${catalogPartIndex}.sites.${siteIndex}.defaultProductionLineId`}
								label={i18n.t("default_production_line")}
								options={productionLinesBySiteId[site.siteId] ?? []}
								getOptionKey={(o) => o.productionLineId}
								getOptionLabel={(o) => o.productionLineName}
							/>
							<FormNumberField
								control={control}
								name={capacityQuantityField}
								label={i18n.t("capacity_quantity")}
							/>
						</>
					)}
				</>
			</HorizontalBox>
		</VerticalBox>
	);
};

interface FormValues {
	catalogParts: CatalogPartFormValues[];
}

interface CatalogPartFormValues {
	catalogPart: CatalogPartView;
	sites: CatalogPartToSiteParams[];
}
